import { createContext, use, useMemo, useId, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';
import { Avo, inspectorInstance } from '@iwt-core-web/avo';

const TrackingProviderContext = createContext(null);
const useTrackingProvider = ()=>{
    const context = use(TrackingProviderContext);
    if (context === null) {
        throw new Error("`useTrackingProvider` must be used within a `TrackingProvider`");
    }
    return context;
};

/**
 * Manages and provides global context to applications.
 *
 * Supplies the event brand and user-consent status through React context.
 */ const TrackingProvider = ({ brand, children, hasUserConsent })=>{
    const contextValue = useMemo(()=>({
            brand,
            hasUserConsent
        }), [
        brand,
        hasUserConsent
    ]);
    return /*#__PURE__*/ jsx(TrackingProviderContext, {
        value: contextValue,
        children: children
    });
};

const scopes = new Map();
let activeScopeId = null;
/** Tracks whether `Avo.initAvo` has run for the currently mounted scope tree. */ let isAvoInitialized = false;
/** Plugins own their credentials, so Avo destinations are made without an api key. */ const NO_API_KEY = "";
/** `AvoEnv.Prod` when `process.env.ENVIRONMENT === "live"`, `AvoEnv.Dev` otherwise. */ const getAvoEnv = ()=>process.env.ENVIRONMENT === "live" ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev;
const CustomDestination = {
    make: ()=>{},
    logEvent: (eventName, eventProperties)=>{
        const scope = activeScopeId === null ? undefined : scopes.get(activeScopeId);
        if (scope === undefined) {
            return;
        }
        // `component` is a legacy alias for `appId` and should not reach plugins.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { component, ...restEventProperties } = eventProperties;
        const scopedProperties = {
            ...restEventProperties,
            brand: scope.brand,
            appId: scope.appId
        };
        for (const plugin of scope.plugins){
            plugin.logEvent?.(eventName, scopedProperties);
        }
    }
};
/**
 * Avo is configured globally, so only the first mounted scope should initialize it.
 * Every `TrackingScope` calls this on mount; the guard makes repeat calls a no-op.
 */ const initAvo = (env, appId)=>{
    if (isAvoInitialized) return;
    isAvoInitialized = true;
    const init = (inspector)=>{
        Avo.initAvo({
            env,
            webDebugger: env === Avo.AvoEnv.Dev,
            ...inspector !== undefined ? {
                inspector
            } : {}
        }, {}, // Route generated Avo events through the active TrackingScope.
        CustomDestination);
    };
    if (env === Avo.AvoEnv.Prod) {
        void inspectorInstance(appId).then(init);
    } else {
        init();
    }
};
const registerTrackingScope = (id, brand, appId, env, plugins)=>{
    scopes.set(id, {
        brand,
        appId,
        plugins
    });
    for (const plugin of plugins){
        plugin.make?.(env, NO_API_KEY);
    }
    return ()=>{
        scopes.delete(id);
        // Last scope torn down: let the next mounted tree re-initialize Avo.
        if (scopes.size === 0) {
            isAvoInitialized = false;
        }
    };
};
const withTrackingScope = (id, callback)=>{
    const previousScopeId = activeScopeId;
    activeScopeId = id;
    try {
        return callback();
    } finally{
        activeScopeId = previousScopeId;
    }
};

const TrackingScopeContext = createContext(null);
const useTrackingScope = ()=>{
    const context = use(TrackingScopeContext);
    if (context === null) {
        throw new Error("`useTrackingScope` must be used within a `TrackingScope`");
    }
    return context;
};

const EMPTY_PLUGINS = [];
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
/** Plugins are identified by `id`, falling back to object identity. */ const getPluginKey = (plugin)=>plugin.id ?? plugin;
/** Keeps the first plugin for each key. */ const dedupePlugins = (plugins)=>{
    const registeredPlugins = new Set();
    return plugins.filter((plugin)=>{
        const key = getPluginKey(plugin);
        if (registeredPlugins.has(key)) return false;
        registeredPlugins.add(key);
        return true;
    });
};
/**
 * Keeps the previous `plugins` reference while its plugin keys are unchanged, so
 * an inline array literal does not re-register the scope on every render.
 */ const useStablePlugins = (plugins)=>{
    const ref = useRef(plugins);
    const previous = ref.current;
    if (previous.length !== plugins.length || previous.some((plugin, index)=>getPluginKey(plugin) !== getPluginKey(plugins[index]))) {
        ref.current = plugins;
    }
    return ref.current;
};
const TrackingScope = ({ plugins = EMPTY_PLUGINS, children, appId: appIdProp, component })=>{
    if (appIdProp !== undefined && component !== undefined) {
        throw new Error("`TrackingScope` accepts either `appId` or `component`, but not both");
    }
    const appId = appIdProp ?? component;
    if (!appId) {
        throw new Error("`TrackingScope` requires either `appId` or `component` prop");
    }
    const env = getAvoEnv();
    const scopeId = useId();
    const { brand, hasUserConsent } = useTrackingProvider();
    const stablePlugins = useStablePlugins(plugins);
    const activePlugins = useMemo(()=>dedupePlugins(stablePlugins).filter((plugin)=>!plugin.isUserConsentRequired || hasUserConsent), [
        stablePlugins,
        hasUserConsent
    ]);
    useEffect(()=>{
        if (component !== undefined) {
            console.warn("`TrackingScope` component prop is deprecated. Use `appId` instead.");
        }
    }, [
        component
    ]);
    // Layout effects run before any child `useEffect`, so the scope is registered
    // before descendants dispatch their first events.
    useIsomorphicLayoutEffect(()=>registerTrackingScope(scopeId, brand, appId, env, activePlugins), [
        scopeId,
        brand,
        appId,
        env,
        activePlugins
    ]);
    // Multiple TrackingScope instances can mount in one app; runtime init is idempotent.
    useIsomorphicLayoutEffect(()=>{
        initAvo(env, appId);
    }, [
        env,
        appId
    ]);
    const contextValue = useMemo(()=>({
            appId,
            scopeId
        }), [
        appId,
        scopeId
    ]);
    return /*#__PURE__*/ jsx(TrackingScopeContext, {
        value: contextValue,
        children: children
    });
};

/**
 * Invokes a tracking event function with its typed properties.
 *
 * Uses a type assertion to bypass TypeScript's limitation in preserving
 * the relationship between event function unions and their corresponding
 * property types. The assertion scope is confined here to maintain type
 * safety at the call site.
 */ const dispatchEvent = (event)=>{
    event.fn(event.props);
};
const useEventFactory = ()=>{
    useTrackingProvider();
    const { scopeId } = useTrackingScope();
    return useCallback(async (input)=>{
        const events = Array.isArray(input) ? input : [
            input
        ];
        withTrackingScope(scopeId, ()=>events.forEach(dispatchEvent));
    }, [
        scopeId
    ]);
};

/**
 * Hook for tracking action events.
 *
 * @returns A callback function that, when invoked, will dispatch all associated action events
 */ const useActionEvent = ()=>{
    return useEventFactory();
};

/**
 * A hook for tracking pageView events.
 *
 * @param path - The current path of the page being viewed (it can also be an identifier if the current view has no dedicated path)
 * @param events - A single or array of { fn, props } event objects to fire on page view
 * @returns void - Events are automatically dispatched when path changes
 */ const usePageViewEvent = (path, events)=>{
    const eventFactory = useEventFactory();
    /**
   * Ref is used to set and retrieve the latest value without impacting the React rendering lifecycle.
   * It avoids extra hook dependency that could lead to unwanted duplicate event submission
   */ const latestRef = useRef({
        eventFactory,
        events
    });
    useEffect(()=>{
        latestRef.current = {
            events,
            eventFactory
        };
    }, [
        eventFactory,
        events
    ]);
    useEffect(()=>{
        const { eventFactory, events } = latestRef.current;
        eventFactory(events);
    }, [
        path
    ]);
};

/**
 * Hook for tracking an element once it enters the viewport.
 * Definition of viewed reference - https://avivgroup.atlassian.net/wiki/spaces/DATA/pages/1443660005/Classifieds+Analytics+Glossary#Viewed-classified-(card)
 *
 * @param eventData - A `{ fn, props }` event object or array to send when the element becomes visible
 * @returns An object containing a ref to attach to the element being tracked
 *
 * @example
 * ```typescript
 * const { ref } = useElementViewEvent({ fn: viewPage, props: pageProperties });
 *
 * return <div ref={ref}>Content to track</div>;
 * ```
 */ const useElementViewEvent = (eventData)=>{
    const trackEvent = useEventFactory();
    const hasTriggeredRef = useRef(false);
    const observerRef = useRef(null);
    const eventDataRef = useRef(eventData);
    useEffect(()=>{
        eventDataRef.current = eventData;
    }, [
        eventData
    ]);
    const ref = useCallback((node)=>{
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!node || hasTriggeredRef.current || typeof window === "undefined" || !("IntersectionObserver" in window)) return;
        // Default options: viewport root, no margin, fires on the first visible pixel.
        const observer = new IntersectionObserver(([entry], observer)=>{
            if (entry?.isIntersecting && !hasTriggeredRef.current) {
                trackEvent(eventDataRef.current);
                observer.disconnect();
                observerRef.current = null;
                hasTriggeredRef.current = true;
            }
        });
        observerRef.current = observer;
        observer.observe(node);
    }, [
        trackEvent
    ]);
    useEffect(()=>{
        return ()=>{
            observerRef.current?.disconnect();
        };
    }, []);
    return {
        ref
    };
};

const createPlugin = (input)=>{
    // Dumb factory currently used now, could be improved when needed.
    return input;
};

/**
 * Builds an event envelope factory with generated `id`, `time` and `specversion`.
 *
 * @deprecated Superseded by generated Avo event functions dispatched through the
 * event hooks. Kept for the Google Analytics event builders until they are removed.
 */ const createEvent = (input)=>{
    return (providerEventMetadata)=>{
        const eventMetadata = {
            ...providerEventMetadata,
            ...input.metadata,
            id: crypto.randomUUID(),
            time: new Date().toISOString(),
            specversion: "1.0"
        };
        return {
            metadata: eventMetadata,
            payload: input.createPayload(eventMetadata)
        };
    };
};

export { TrackingProvider, TrackingScope, createEvent, createPlugin, useActionEvent, useElementViewEvent, usePageViewEvent };
